import DOMPurify from 'dompurify'

/**
 * Calculates the volume in cubic feet from dimensions in inches
 * @param {number} length - Length in inches
 * @param {number} width - Width in inches
 * @param {number} height - Height in inches
 * @returns {number|null} Volume in cubic feet, or null if any dimension is missing
 */
export const calculateVolumeCubicFeet = (length, width, height) => {
    if (!length || !width || !height) {
        return null;
    }
    return ((length * width * height) / 1728).toFixed(3); // 1728 cubic inches = 1 cubic foot
}; 

export const calculateNetCaseWeight = (itemSize, itemSizeUnits, casePack) => {
    if (!itemSize || !casePack) return 0;
    let weight = itemSize * casePack;
    if (itemSizeUnits === 'oz') {
        weight /= 16; // Convert ounces to pounds
    }
    return weight.toFixed(2);
};

export const calculateGrossCaseWeight = (itemSize, itemSizeUnits, casePack, packageWeight, packageWeightUnits) => {
    const netCaseWeight = calculateNetCaseWeight(itemSize, itemSizeUnits, casePack);
    if (!packageWeight) return netCaseWeight;
    let pkgWeight = packageWeight;
    if (packageWeightUnits === 'oz') {
        pkgWeight /= 16; // Convert ounces to pounds
    }
    return (parseFloat(netCaseWeight) + parseFloat(pkgWeight)).toFixed(2);
};

export const highlightText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    // Sanitize the search term to escape special regex characters
    const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Create a regular expression that matches the search term case-insensitively
    const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');

    // Replace matches with highlighted spans
    const highlightedText = text.toString().replace(regex, '<mark>$1</mark>');

    // Sanitize the HTML to prevent XSS attacks
    return <span dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(highlightedText)
    }} />;
};

/**
 * Parses a date-only string (YYYY-MM-DD) as a local Date to avoid timezone shifts
 * Falls back to native Date parsing for other formats
 */
export const parseDateOnly = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const str = String(value);
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (match) {
        const year = Number(match[1]);
        const month = Number(match[2]);
        const day = Number(match[3]);
        return new Date(year, month - 1, day);
    }
    return new Date(value);
};

export const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });
};

export const formatDate = (dateString) => {
    if (!dateString) return '';
    // Use parseDateOnly for date-only strings (YYYY-MM-DD) to avoid timezone shifts
    const date = parseDateOnly(dateString);
    return date ? date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }) : '';
};

/**
 * Formats a currency amount with thousand separators and proper currency symbol
 * @param {number|string} amount - The amount to format
 * @param {string} [currency='USD'] - The currency code (default: 'USD')
 * @returns {string} - The formatted currency string (e.g., "$19,326.50")
 */
/**
 * Formats a quantity value, showing decimals only when needed
 * @param {number|string} quantity - The quantity to format
 * @returns {number|string} - Integer if whole number, otherwise fixed to up to 4 decimal places
 */
export const formatQuantity = (quantity) => {
    if (quantity === null || quantity === undefined || quantity === '') {
        return '';
    }
    const num = parseFloat(quantity);
    if (isNaN(num)) {
        return '';
    }
    return num % 1 === 0 ? num : parseFloat(num.toFixed(4));
};

export const formatCurrency = (amount, currency = 'USD') => {
    if (amount === null || amount === undefined || amount === '') {
        return '';
    }
    
    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount)) {
        return '';
    }
    
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
    }).format(numericAmount);
};

export const formatUnitCost = (amount, currency = 'USD') => {
    if (amount === null || amount === undefined || amount === '') return '';
    const num = parseFloat(amount);
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
    }).format(num);
};

/**
 * Formats a date-only value for display using US format (MM/DD/YYYY), returning empty string when empty
 */
export const formatDateOnly = (value) => {
    const date = parseDateOnly(value);
    return date ? date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }) : '';
};

/**
 * Parses a date value and returns it as a YYYY-MM-DD string for HTML5 date inputs
 * Handles various input formats and avoids timezone shifts
 */
export const parseDateToString = (value) => {
    if (!value) return '';

    // If already in YYYY-MM-DD format, return as-is
    const str = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        return str;
    }

    // Parse to Date object and convert to YYYY-MM-DD in local timezone
    const date = parseDateOnly(value);
    if (!date || isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

/**
 * Formats a quantity for display, showing cases with items in parentheses for items with case_pack > 1
 * @param {number} quantity - The quantity in items (base unit)
 * @param {number} casePack - The case pack size (items per case), defaults to 1
 * @param {object} options - Optional configuration
 * @param {boolean} options.isRate - If true, treats as a rate (e.g., items/day) and shows decimals
 * @param {string} options.rateUnit - Unit suffix for rates (default: '/day')
 * @returns {string} - Formatted string: "X cases (Y items)" or "Y items"
 */
export const formatQuantityWithCases = (quantity, casePack = 1, options = {}) => {
    const { isRate = false, rateUnit = '/day' } = options;

    if (quantity === null || quantity === undefined) {
        return 'N/A';
    }

    const numQuantity = Number(quantity);

    // If case_pack is 1 or not set, just show items
    if (!casePack || casePack <= 1) {
        if (isRate) {
            return `${numQuantity.toFixed(2)} items${rateUnit}`;
        }
        return `${Math.round(numQuantity).toLocaleString()} items`;
    }

    // Calculate cases (show decimal)
    const cases = numQuantity / casePack;

    if (isRate) {
        return `${cases.toFixed(2)} cases${rateUnit} (${numQuantity.toFixed(2)} items${rateUnit})`;
    }

    return `${cases.toFixed(2)} cases (${Math.round(numQuantity).toLocaleString()} items)`;
};

